import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cx } from './cx.js'
import { Icon, type IconName } from '../icons/index.js'

// Buttons ------------------------------------------------------------------------

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: IconName
  iconRight?: IconName
  loading?: boolean
}

/*
 * primary: the one accent, filled, white text. secondary: surface + hairline.
 * ghost: text only. soft is a quieter secondary (sunken fill, no border);
 * danger is secondary with red text.
 */
const variantCls: Record<ButtonVariant, string> = {
  primary: 'border border-transparent bg-accent-fill text-on-accent hover:bg-[var(--accent-fill-hover)] disabled:opacity-50',
  secondary: 'border border-line-strong bg-surface text-ink hover:border-field hover:bg-surface-2 disabled:text-ink-3 disabled:hover:bg-surface',
  soft: 'border border-transparent bg-sunken text-ink hover:bg-surface-2 disabled:text-ink-3',
  ghost: 'border border-transparent text-ink-2 hover:bg-sunken hover:text-ink disabled:text-ink-3',
  danger: 'border border-line-strong bg-surface text-danger hover:border-[color-mix(in_oklab,var(--danger)_45%,transparent)] hover:bg-danger-soft disabled:opacity-50',
}
/* 8px radius; 32px and 36px tall (lg is md with more padding for hero actions) */
const sizeCls: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-[8px]',
  md: 'h-9 px-3.5 text-[13.5px] gap-2 rounded-[8px]',
  lg: 'h-9 px-4 text-[14px] gap-2 rounded-[8px]',
}

const buttonBase = 'rh-press inline-flex select-none items-center justify-center whitespace-nowrap font-medium tracking-[-0.005em] disabled:cursor-not-allowed'

/** Button look for links (router links, anchors) so they match `Button`. */
export function buttonClasses(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string): string {
  return cx(buttonBase, variantCls[variant], sizeCls[size], className)
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, iconRight, loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClasses(variant, size, className)}
      {...rest}
    >
      {loading ? <Spinner size={14} className="-ml-0.5" /> : icon ? <Icon name={icon} size={14} className="-ml-0.5 shrink-0" /> : null}
      {children}
      {iconRight && <Icon name={iconRight} size={14} className="-mr-0.5 shrink-0" />}
    </button>
  )
})

export function IconButton({ icon, label, className, size = 36, ...rest }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & { icon: IconName; label: string; size?: number }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx('rh-hit inline-flex shrink-0 items-center justify-center rounded-[8px] text-ink-2 transition-colors hover:bg-sunken hover:text-ink', className)}
      style={{ width: size, height: size }}
      {...rest}
    >
      <Icon name={icon} size={Math.min(18, Math.round(size * 0.45))} />
    </button>
  )
}

export function Spinner({ size = 14, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={cx('animate-spin', className)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}
